/* Page scroll lock — one owner, reference counted.
 *
 * Before this file every drawer in the theme wrote `document.body.style.overflow`
 * directly: the cart, the mobile menu, the quick view, the filter drawer, the search
 * modal, the size chart, the zoom drawer, the address forms and the chat widget. Nine
 * writers, one variable, no reference count — so closing any one of them unlocked the
 * page while the others were still open, and any one of them forgetting to restore it
 * left the customer on a page that would not scroll until they reloaded.
 *
 * Everything now goes through acquire(id) / release(id). The page is locked while at
 * least one holder is registered and released the moment the last one lets go.
 *
 * The lock itself is a class on <html>, never an inline style, so it cannot be half
 * cleared: `overflow: hidden` restored as `overflow: auto` is NOT the same value as the
 * stylesheet's and on iOS Safari it makes <body> its own scroll container, which freezes
 * touch scrolling. A class has no such middle state.
 *
 * The lock is overflow only. Pinning <body> with position: fixed is the older iOS
 * recipe, and it costs the customer their place: the page collapses to the top the
 * moment it is pinned and has to be scrolled back on release, which reads as a jump
 * every single time the drawer closes. iOS 16+ honours overflow: hidden on the root, so
 * the pin buys nothing here and the reading position is simply never disturbed.
 */
(function () {
  'use strict';

  var holders = new Set();

  function apply() {
    var html = document.documentElement;
    if (holders.size > 0) {
      if (html.classList.contains('is-scroll-locked')) return;
      html.classList.add('is-scroll-locked');
      // kept for anything still styling off the old hook
      document.body.classList.add('drawer-open');
    } else {
      if (!html.classList.contains('is-scroll-locked')) return;
      html.classList.remove('is-scroll-locked');
      document.body.classList.remove('drawer-open');
      // Left over from the position: fixed technique this no longer uses. Cleared so a
      // page served from cache with the old inline offset still lands upright.
      if (document.body.style.top) document.body.style.top = '';
      // Anything that wrote the old inline style before this module existed, or that a
      // third-party script sets, is cleared here too — this is the only place allowed to
      // decide the page scrolls again.
      if (document.body.style.overflow) document.body.style.overflow = '';
    }
  }

  var ScrollLock = {
    acquire: function (id) {
      if (!id) return;
      holders.add(id);
      apply();
    },
    release: function (id) {
      if (!id) return;
      holders.delete(id);
      apply();
    },
    releaseAll: function () {
      holders.clear();
      apply();
    },
    has: function (id) {
      return holders.has(id);
    },
    holders: function () {
      return Array.from(holders);
    }
  };

  /* Reconciler.
   *
   * A holder can only leak if the component that took it is destroyed or throws before
   * releasing. Rather than trust every caller, this checks the one thing that cannot
   * lie — whether any panel is actually on screen — and lets go if none is. It runs only
   * while a lock is held, so it costs nothing on a normal page.
   *
   * .modal-container alone is not the drawers: the desktop mega-menu panels wear that
   * class too and sit at display:grid permanently (they reveal with opacity). The real
   * drawers all add .full_screen.
   */
  function anyPanelVisible() {
    var panels = document.querySelectorAll('.modal-container.full_screen, [data-scroll-lock-panel]');
    for (var i = 0; i < panels.length; i++) {
      var el = panels[i];
      var cs = getComputedStyle(el);
      if (cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0'
          && el.getBoundingClientRect().width > 0) {
        return true;
      }
    }
    return false;
  }

  setInterval(function () {
    if (holders.size === 0) return;
    if (!anyPanelVisible()) ScrollLock.releaseAll();
  }, 700);

  // Restoring from the bfcache brings back the DOM, inline styles and classes together.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) ScrollLock.releaseAll();
  });

  window.ScrollLock = ScrollLock;
})();
